
import { useState } from 'react';
import { useToast } from '@/hooks/toast';
import { supabase } from '@/integrations/supabase/client';
import { useAutoStockManagement } from './useAutoStockManagement';

/**
 * Hook for managing listing stock operations using Supabase database functions
 * @returns Object with functions to handle stock operations
 */
export const useStockManagement = () => {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const { autoDeactivateOnZeroStock } = useAutoStockManagement();

  /**
   * Decrease stock when a purchase is made using RPC function
   * @param listingId The ID of the listing
   * @param quantity The quantity to decrease (default: 1)
   * @param trackOrder Whether to track this order for later references (cancellations, etc)
   * @param orderId Optional order ID for tracking
   * @returns Promise<boolean> indicating success or failure
   */
  const handleDecrementStock = async (
    listingId: number, 
    quantity: number = 1, 
    trackOrder: boolean = false, 
    orderId?: string
  ): Promise<boolean> => {
    setIsProcessing(true);

    try {
      const { data: currentListing, error: fetchError } = await supabase
        .from('listings')
        .select('stock, is_active')
        .eq('id', listingId)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      const currentStock = currentListing?.stock;

      if (currentStock === undefined || currentStock < quantity) {
        toast({
          title: "Gabim",
          description: "Nuk ka stok të mjaftueshëm për këtë produkt.",
          variant: "destructive"
        });
        return false;
      }

      const { data, error } = await supabase.rpc(
        'update_listing_stock_decrement',
        { p_listing_id: listingId, p_decrement: quantity }
      );

      if (error) throw error;

      if (!data) {
        toast({
          title: "Gabim",
          description: "Nuk ka stok të mjaftueshëm për këtë produkt.",
          variant: "destructive"
        });
        return false;
      }

      if (trackOrder && orderId) {
        try {
          const { error: trackError } = await supabase
            .from('stock_transactions')
            .insert({
              listing_id: listingId,
              quantity: -quantity,
              order_id: orderId,
              is_canceled: false
            });
          if (trackError) {
            toast({
              title: "Information",
              description: "Stoku u përditësua, por transaksioni nuk u regjistrua.",
              variant: "default"
            });
          }
        } catch (trackErr) {
          toast({
            title: "Information",
            description: "Stoku u përditësua, por transaksioni nuk u regjistrua.",
            variant: "default"
          });
        }
      }

      const newStock = currentStock - quantity;

      if (newStock <= 0 && currentListing.is_active) {
        await autoDeactivateOnZeroStock(listingId, newStock);
      }

      return true;
    } catch (error) {
      toast({
        title: "Gabim",
        description: "Ndodhi një gabim gjatë përditësimit të stokut.",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleIncrementStock = async (
    listingId: number, 
    quantity: number = 1,
    orderId?: string
  ): Promise<boolean> => {
    setIsProcessing(true);

    try {
      console.log(`Attempting to increase stock for listing ${listingId} by ${quantity}`);

      // If we have an orderId, check if there's already a canceled transaction for this order
      if (orderId) {
        const { data: existingTransaction } = await supabase
          .from('stock_transactions')
          .select('*')
          .eq('order_id', orderId)
          .eq('listing_id', listingId)
          .eq('is_canceled', true)
          .single();

        if (existingTransaction) {
          console.log('This order was already canceled and stock has been restored');
          toast({
            title: "Informacion",
            description: "Stoku për këtë porosi është tashmë rikthyer.",
          });
          return true; // Stock was already restored, so avoid double incrementing
        }

        // Check if there's an active (not canceled) transaction that we need to cancel
        const { data: activeTransactions } = await supabase
          .from('stock_transactions')
          .select('*')
          .eq('order_id', orderId)
          .eq('listing_id', listingId)
          .eq('is_canceled', false);

        if (!activeTransactions || activeTransactions.length === 0) {
          console.log('No active transaction found for this order');
          toast({
            title: "Gabim",
            description: "Nuk u gjet transaksion aktiv për këtë porosi.",
            variant: "destructive"
          });
          return false;
        }
      }

      // Get current stock before update
      const { data: currentListing, error: fetchError } = await supabase
        .from('listings')
        .select('stock')
        .eq('id', listingId)
        .single();

      if (fetchError) {
        console.error('Error fetching current stock:', fetchError);
        throw fetchError;
      }

      const previousStock = currentListing?.stock || 0;
      console.log(`Previous stock for listing ${listingId}: ${previousStock}`);

      // Use Supabase RPC to safely update stock (transaction-safe)
      const { data, error } = await supabase.rpc(
        'update_listing_stock_increment',
        { p_listing_id: listingId, p_increment: quantity }
      );

      if (error) {
        console.error('Error in update_listing_stock_increment RPC:', error);
        throw error;
      }

      if (!data) {
        console.error('Failed to update stock');
        return false;
      }

      // If we have an order ID, mark the related transaction as canceled
      if (orderId) {
        const { error: updateError } = await supabase
          .from('stock_transactions')
          .update({ is_canceled: true })
          .eq('order_id', orderId)
          .eq('listing_id', listingId)
          .eq('is_canceled', false); // Only update active transactions

        if (updateError) {
          console.error('Error updating stock transaction cancellation status:', updateError);
        } else {
          console.log(`Successfully marked transactions for order ${orderId} as canceled`);
        }
      }

      console.log(`Successfully increased stock for listing ${listingId} by ${quantity}`);

      // Calculate new stock after increment
      const newStock = previousStock + quantity;

      // If previous stock was 0 and we're adding stock, check if we should reactivate the listing
      if (previousStock <= 0 && newStock > 0) {
        // Get listing activation status
        const { data: listingData, error: listingError } = await supabase
          .from('listings')
          .select('is_active')
          .eq('id', listingId)
          .single();

        if (!listingError && listingData && !listingData.is_active) {
          // Listing is inactive, so reactivate it
          const { error: updateError } = await supabase
            .from('listings')
            .update({ 
              is_active: true, 
              last_active_at: new Date().toISOString() 
            })
            .eq('id', listingId);

          if (!updateError) {
            toast({
              title: "Shpallja u riaktivizua",
              description: "Shpallja juaj u riaktivizua automatikisht sepse stoku u rrit nga 0.",
            });
          }
        }
      }

      return true;
    } catch (error) {
      console.error('Error increasing stock:', error);
      toast({
        title: "Gabim",
        description: "Ndodhi një gabim gjatë përditësimit të stokut.",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  const setStockValue = async (listingId: number, newStock: number): Promise<boolean> => {
    setIsProcessing(true);

    try {
      const { data: currentListing, error: fetchError } = await supabase
        .from('listings')
        .select('stock')
        .eq('id', listingId)
        .single();

      if (fetchError) throw fetchError;

      const { error } = await supabase
        .from('listings')
        .update({ stock: newStock })
        .eq('id', listingId);

      if (error) throw error;

      if (newStock <= 0) {
        await autoDeactivateOnZeroStock(listingId, newStock);
      }

      return true;
    } catch (error) {
      toast({
        title: "Gabim",
        description: "Ndodhi një gabim gjatë përditësimit të stokut.",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    isProcessing,
    handleDecrementStock,
    handleIncrementStock,
    setStockValue
  };
};
